/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Face Swap 首屏图片切换展示器 - 用于工具区右侧空状态展示
 */
import Image from 'next/image';
import React, { useCallback, useRef, useState } from 'react';

const ASSET_PREFIX = 'https://assets.falcocut.ai/face-swap/';

interface ShowcaseItem {
  id: string;
  mainImage: string;
  thumbnail: string;
}

export const showcaseItems: ShowcaseItem[] = [
  {
    id: 'group-photo',
    mainImage: `${ASSET_PREFIX}group-photo-face-swap-street.webp`,
    thumbnail: `${ASSET_PREFIX}group-photo-face-swap-street-thumbnail.webp`,
  },
  {
    id: 'selfie',
    mainImage: `${ASSET_PREFIX}selfie-face-swap-fitting-room.webp`,
    thumbnail: `${ASSET_PREFIX}selfie-face-swap-fitting-room-thumbnail.webp`,
  },
  {
    id: 'live-stream',
    mainImage: `${ASSET_PREFIX}live-stream-face-swap.webp`,
    thumbnail: `${ASSET_PREFIX}live-stream-face-swap-thumbnail.webp`,
  },
  {
    id: 'beauty-ad',
    mainImage: `${ASSET_PREFIX}beauty-ad-face-swap.webp`,
    thumbnail: `${ASSET_PREFIX}beauty-ad-face-swap-thumbnail.webp`,
  },
  {
    id: 'ecommerce',
    mainImage: `${ASSET_PREFIX}ecommerce-model-face-swap.webp`,
    thumbnail: `${ASSET_PREFIX}ecommerce-model-face-swap-thumbnail.webp`,
  },
];

export const DEFAULT_INDEX = 2;

/** Default preload image URL (index 2 main image) */
export const SHOWCASE_PRELOAD_IMAGE = showcaseItems[DEFAULT_INDEX].mainImage;

// Inline SVG icons (no antd)
const ChevronLeftIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface FaceSwapShowcaseProps {
  className?: string;
}

const FaceSwapShowcase: React.FC<FaceSwapShowcaseProps> = ({ className = '' }) => {
  const [activeIndex, setActiveIndex] = useState(DEFAULT_INDEX);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback((index: number) => {
    const next = (index + showcaseItems.length) % showcaseItems.length;
    setActiveIndex(next);
    if (thumbnailContainerRef.current) {
      const children = thumbnailContainerRef.current.children;
      const target = children[next] as HTMLElement | undefined;
      target?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, []);

  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  const activeItem = showcaseItems[activeIndex];

  return (
    <div className={`flex flex-col overflow-hidden ${className}`}>
      {/* 主图区域 - flex-1 自适应压缩，确保不超出容器 */}
      <div className="relative flex-1 min-h-0 rounded-2xl overflow-hidden mx-3 mt-3 mb-2">
        <Image
          src={activeItem.mainImage}
          alt={`Face swap example - ${activeItem.id}`}
          fill
          className="object-contain"
          priority={activeIndex === DEFAULT_INDEX}
          sizes="(max-width: 768px) 100vw, 600px"
        />

        {/* 右上角切换箭头 */}
        <div className="absolute top-2 right-2 flex gap-1.5">
          <button
            type="button"
            onClick={goPrev}
            className="w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:bg-white transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:bg-white transition-colors"
            aria-label="Next image"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      {/* 缩略图条 - flex-shrink-0 固定高度，不被压缩 */}
      <div className="flex-shrink-0 flex items-center justify-center gap-2 px-3 pb-3">
        {/* 左箭头 */}
        <button
          type="button"
          onClick={goPrev}
          className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Previous"
        >
          <ChevronLeftIcon />
        </button>

        {/* 缩略图 - 固定高度 60px，1:1 比例 */}
        <div ref={thumbnailContainerRef} className="flex gap-1.5 overflow-hidden">
          {showcaseItems.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goTo(index)}
              className={`
                relative flex-shrink-0 rounded-md overflow-hidden transition-all duration-200 box-border
                ${
                  index === activeIndex
                    ? 'border-2 border-[#7C3AED]'
                    : 'border-2 border-transparent hover:border-gray-300'
                }
              `}
              style={{ width: '60px', height: '60px' }}
              aria-label={`View ${item.id} face swap example`}
            >
              <Image
                src={item.thumbnail}
                alt={`Thumbnail - ${item.id}`}
                fill
                className="object-cover"
                loading="lazy"
                sizes="60px"
              />
            </button>
          ))}
        </div>

        {/* 右箭头 */}
        <button
          type="button"
          onClick={goNext}
          className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Next"
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

export default FaceSwapShowcase;
